<script lang="ts">
  /**
   * The settings that belong to the household rather than to one portal.
   *
   * HA Portal held the notice, the kill switches and the model choice in its own
   * Remote Config, where no other portal could read them and only that portal's
   * console could change them. They live here now. What stayed behind stayed on
   * purpose: HA's calendars, alarm, automations, buttons, passes and twins are
   * meaningless outside that portal, and nothing secret is here at all — this
   * document is served to every signed-in browser.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type Notice = { text: string; severity: "info" | "warn"; until: string | null };
  type Household = { notice: Notice | null; flags: Record<string, boolean>; models: Record<string, string> };

  let data = $state<Household | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let saving = $state(false);
  let saved = $state(false);

  let noticeOn = $state(false);
  let text = $state("");
  let severity = $state<"info" | "warn">("info");
  let until = $state("");
  let flagRows = $state<{ key: string; on: boolean }[]>([]);
  let modelRows = $state<{ key: string; value: string }[]>([]);
  let newFlag = $state("");
  let newModelKey = $state("");
  let newModelValue = $state("");

  async function load() {
    loading = true;
    error = null;
    try {
      const d = await api.get<Household>("/household");
      data = d;
      noticeOn = d.notice !== null;
      text = d.notice?.text ?? "";
      severity = d.notice?.severity ?? "info";
      until = (d.notice?.until ?? "").slice(0, 10);
      flagRows = Object.entries(d.flags).map(([key, on]) => ({ key, on }));
      modelRows = Object.entries(d.models).map(([key, value]) => ({ key, value }));
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load household settings.";
    } finally {
      loading = false;
    }
  }
  void load();

  function addFlag() {
    const k = newFlag.trim();
    if (!k || flagRows.some((f) => f.key === k)) return;
    // Off by default. A kill switch that arrives already on has killed something.
    flagRows = [...flagRows, { key: k, on: false }];
    newFlag = "";
  }

  function addModel() {
    const k = newModelKey.trim();
    if (!k || modelRows.some((m) => m.key === k)) return;
    modelRows = [...modelRows, { key: k, value: newModelValue.trim() }];
    newModelKey = "";
    newModelValue = "";
  }

  async function save() {
    if (noticeOn && !text.trim()) {
      error = "A notice needs something to say.";
      return;
    }
    saving = true;
    error = null;
    saved = false;
    try {
      const body = {
        notice: noticeOn ? { text: text.trim(), severity, until: until || null } : null,
        flags: Object.fromEntries(flagRows.map((f) => [f.key, f.on])),
        models: Object.fromEntries(modelRows.filter((m) => m.value.trim()).map((m) => [m.key, m.value.trim()])),
      };
      // Guarded: a kill switch reaches every portal at once.
      const ok = await stepUp.guard((confirm) => api.put("/household", { ...body, confirm }), "Household settings");
      if (ok) {
        saved = true;
        await load();
      }
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not save that.";
    } finally {
      saving = false;
    }
  }
</script>

<div class="wrap">
  <section class="card">
    <div class="head">
      <span class="kicker">Household notice</span>
      {#if saved}<span class="sub ok"><Icon name="check" size={11} /> saved</span>{/if}
    </div>
    <p class="sub">
      Shown on every portal, not just one — a banner that appears in a single place is a
      banner nobody sees.
    </p>

    {#if loading}
      <p class="sub pad">Loading…</p>
    {:else}
      <label class="chk">
        <input type="checkbox" bind:checked={noticeOn} />
        <span>Show a notice</span>
      </label>

      {#if noticeOn}
        <div class="grid">
          <label class="f f--wide">
            <span>What it says</span>
            <input bind:value={text} placeholder="Fibre is down — the portals are running on LTE." />
          </label>
          <label class="f">
            <span>Weight</span>
            <select bind:value={severity}>
              <option value="info">Worth knowing</option>
              <option value="warn">Needs attention</option>
            </select>
          </label>
          <label class="f">
            <span>Stop showing after</span>
            <input type="date" bind:value={until} />
          </label>
        </div>
        <p class="sub">
          Leaving the date blank leaves it up until you take it down. A notice with an end
          date disappears on its own, which is the one that actually gets used.
        </p>
      {/if}
    {/if}
  </section>

  <section class="card">
    <div class="head"><span class="kicker">Kill switches</span></div>
    <p class="sub">
      Each portal decides what to do with these. Turning one on takes effect on the next
      load everywhere — there is no per-portal override, deliberately.
    </p>

    {#each flagRows as f (f.key)}
      <label class="row">
        <input type="checkbox" bind:checked={f.on} />
        <code>{f.key}</code>
        <span class="sub">{f.on ? "on" : "off"}</span>
      </label>
    {/each}

    <div class="addrow">
      <input bind:value={newFlag} placeholder="new_switch_name" onkeydown={(e) => e.key === "Enter" && addFlag()} />
      <button class="lnk" onclick={addFlag}>Add</button>
    </div>
  </section>

  <section class="card">
    <div class="head"><span class="kicker">Models</span></div>
    <p class="sub">
      Which model a shared feature uses. Portal-specific prompts stay in their portal; only
      the choice of model is household-wide.
    </p>

    {#each modelRows as m (m.key)}
      <div class="row">
        <code>{m.key}</code>
        <input class="grow" bind:value={m.value} />
      </div>
    {/each}

    <div class="addrow">
      <input bind:value={newModelKey} placeholder="feature" />
      <input bind:value={newModelValue} placeholder="model id" />
      <button class="lnk" onclick={addModel}>Add</button>
    </div>
  </section>

  {#if error}
    <p class="err"><Icon name="alert" size={12} /> {error}</p>
  {/if}

  <div class="acts">
    <button class="btn-primary" onclick={save} disabled={saving || loading}>
      {saving ? "Saving…" : "Save household settings"}
    </button>
  </div>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card {
    padding: 16px 18px 18px;
    border: 1px solid var(--ln);
    border-radius: var(--r-card, 12px);
    background: var(--surface-1, var(--bg));
  }
  .head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
  .kicker { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: var(--acc); }
  .pad { padding: 8px 0; }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin: 10px 0; }
  .f { display: flex; flex-direction: column; gap: 4px; font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .f--wide { grid-column: 1 / -1; }
  .f input, .f select, .addrow input, .row input[type="text"], .row .grow {
    padding: 8px 10px;
    font-size: 0.7812rem;
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
  }
  .chk { display: flex; align-items: center; gap: 8px; font-size: 0.75rem; color: var(--mut); margin: 6px 0; }
  .row { display: flex; align-items: center; gap: 10px; padding: 7px 0; }
  .grow { flex: 1; min-width: 0; }
  .addrow { display: flex; gap: 8px; align-items: center; margin-top: 8px; }
  code {
    padding: 2px 6px;
    font-family: var(--mono, ui-monospace, monospace);
    font-size: 0.72rem;
    color: var(--tx);
    background: var(--surface-2, var(--bg));
    border-radius: 5px;
  }
  .acts { display: flex; justify-content: flex-end; }
  .ok { color: var(--ok, var(--acc)); display: inline-flex; align-items: center; gap: 4px; }
  .err { display: inline-flex; align-items: center; gap: 5px; margin: 0; font-size: 0.72rem; font-weight: 700; color: var(--warn); }
  .sub { font-size: 0.72rem; color: var(--mut); margin: 0; }
</style>
